import React, { useEffect, useMemo, useState } from "react"
import { Capacitor, registerPlugin } from "@capacitor/core"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import PrintIcon from "@mui/icons-material/Print"
import EditIcon from "@mui/icons-material/Edit"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import StoreIcon from "@mui/icons-material/Store"
import PersonIcon from "@mui/icons-material/Person"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import MainLayout from "../../layouts/MainLayout"
import { useNavigate, useParams } from "react-router-dom"
import { getSaleById } from "../../services/salesService"

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000"

const API_URL = API_BASE_URL.endsWith("/api")
  ? API_BASE_URL
  : `${API_BASE_URL}/api`

const APP_NAME = "RLSys"
const APP_VERSION = "v1.0.0"
const APP_CONTEXT = "ERP entreprise"

const SunmiPrinter = registerPlugin("SunmiPrinter")

const AGENT_VALIDATION_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
]

const SALES_UI = {
  surface: "#ffffff",
  border: "#e2e8f0",
  soft: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
  primary: "#2563eb",
  success: "#16a34a",
  warning: "#ea580c",
  danger: "#dc2626",
}

function getCurrentRole() {
  return localStorage.getItem("kongosys_role") || ""
}

function buildHeaders() {
  return {
    "Content-Type": "application/json",
    "x-kongosys-user-id": localStorage.getItem("kongosys_user_id") || "",
    "x-user-id": localStorage.getItem("kongosys_user_id") || "",
    "x-kongosys-role": localStorage.getItem("kongosys_role") || "",
    "x-user-role": localStorage.getItem("kongosys_role") || "",
    "x-kongosys-company-id": localStorage.getItem("kongosys_company_id") || "1",
    "x-company-id": localStorage.getItem("kongosys_company_id") || "1",
    "x-kongosys-organization-id":
      localStorage.getItem("kongosys_organization_id") || "",
    "x-organization-id": localStorage.getItem("kongosys_organization_id") || "",
    "x-kongosys-organization-code":
      localStorage.getItem("kongosys_organization_code") || "",
    "x-organization-code":
      localStorage.getItem("kongosys_organization_code") || "",
    "x-kongosys-store-id": localStorage.getItem("kongosys_store_id") || "",
    "x-store-id": localStorage.getItem("kongosys_store_id") || "",
    "x-kongosys-warehouse-id":
      localStorage.getItem("kongosys_warehouse_id") || "",
    "x-warehouse-id": localStorage.getItem("kongosys_warehouse_id") || "",
  }
}

async function validateAgentOpportunity(saleId, payload) {
  const response = await fetch(
    `${API_URL}/sales/${saleId}/validate-agent-opportunity`,
    {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
    }
  )

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data?.detail || "Erreur validation demande agent.")
  }

  return data
}

async function confirmAgentDelivery(saleId, payload = {}) {
  const response = await fetch(
    `${API_URL}/sales/${saleId}/confirm-agent-delivery`,
    {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
    }
  )

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data?.detail || "Erreur confirmation livraison.")
  }

  return data
}

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function nearlyEqual(a, b) {
  return Math.abs(toNumber(a) - toNumber(b)) <= 0.005
}

function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatQuantity(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

function formatDateTime(value) {
  if (!value) return "-"

  try {
    return new Date(value).toLocaleString("fr-FR")
  } catch {
    return "-"
  }
}

function safeTicketText(value, fallback = "-") {
  const text = String(value || "").trim()
  return text || fallback
}

function centerTicketLine(text, width = 32) {
  const value = String(text || "").trim()

  if (value.length >= width) {
    return value
  }

  const left = Math.floor((width - value.length) / 2)
  return `${" ".repeat(left)}${value}`
}

function getStatusColor(status) {
  const value = String(status || "").toLowerCase()

  if (value === "paid" || value === "confirmed") return "success"
  if (value === "pending") return "info"
  if (value === "paid_pending_delivery") return "warning"
  if (value === "pending_price_validation") return "warning"
  if (value === "partial") return "warning"
  if (value === "unpaid" || value === "cancelled") return "error"

  return "default"
}

function isAgentOpportunity(sale) {
  return (
    sale?.created_by_role === "agent_commercial" ||
    sale?.order_source === "agent_commercial"
  )
}

function getStatusLabel(status, sale) {
  const value = String(status || "").toLowerCase()

  if (value === "pending_price_validation") return "Décision Entreprise principale"

  if (value === "paid_pending_delivery") {
    return "Payé / attente livraison"
  }

  if (value === "pending" && isAgentOpportunity(sale)) {
    if (sale?.payment_status === "paid") {
      return "Payé / attente livraison"
    }

    return "Prix décidé / attente paiement"
  }

  if (value === "pending") return "En attente"
  if (value === "confirmed") return "Vente confirmée"
  if (value === "cancelled") return "Refusée / annulée"
  if (value === "paid") return "Payé"
  if (value === "partial") return "Partiel"
  if (value === "unpaid") return "Non payé"

  return status || "-"
}

function canEditSale(sale) {
  const role = getCurrentRole()

  if (["platform_admin", "super_admin", "gestionnaire_operations"].includes(role)) {
    return sale.status !== "cancelled"
  }

  if (role === "gerant_magasin" || role === "gerant_depot_relais") {
    return (
      sale.sale_type === "order" ||
      sale.status === "pending" ||
      sale.payment_status === "unpaid"
    )
  }

  return false
}

function canValidateAgentOpportunity(sale) {
  const role = getCurrentRole()

  return (
    AGENT_VALIDATION_ROLES.includes(role) &&
    isAgentOpportunity(sale) &&
    sale.status === "pending_price_validation" &&
    sale.payment_status === "unpaid"
  )
}

function canConfirmAgentDelivery(sale) {
  const role = getCurrentRole()

  return (
    AGENT_VALIDATION_ROLES.includes(role) &&
    isAgentOpportunity(sale) &&
    sale.payment_status === "paid" &&
    ["pending", "paid_pending_delivery"].includes(sale.status)
  )
}

function buildValidationRows(items) {
  return items.map((item) => {
    const baseUnitPrice = toNumber(
      item.base_unit_price || item.selling_price || item.unit_price
    )

    const requestedUnitPrice = toNumber(item.requested_unit_price)
    const finalUnitPrice = toNumber(item.unit_price || baseUnitPrice)
    const quantity = toNumber(item.quantity)

    return {
      sale_item_id: item.id,
      product_code: item.product_code,
      product_name: item.product_name,
      quantity,
      base_unit_price: baseUnitPrice,
      requested_unit_price: requestedUnitPrice > 0 ? requestedUnitPrice : "",
      final_unit_price: finalUnitPrice > 0 ? finalUnitPrice : baseUnitPrice,
      discount_type: item.discount_type || "none",
      discount_value: toNumber(item.discount_value),
    }
  })
}

function getBestWarehouseForItem(item) {
  const options = Array.isArray(item?.warehouse_options)
    ? item.warehouse_options
    : []

  const quantity = toNumber(item?.quantity)

  const eligible = options.filter(
    (option) => toNumber(option.available_quantity) >= quantity
  )

  if (eligible.length === 1) {
    return String(eligible[0].warehouse_id)
  }

  return ""
}

function buildThermalSaleTicket(sale, items) {
  const width = 32

  const totalItems = items.reduce(
    (sum, item) => sum + toNumber(item.quantity),
    0
  )

  const subTotal = items.reduce(
    (sum, item) => sum + toNumber(item.total_price),
    0
  )

  const totalAmount = toNumber(sale?.total_amount)
  const paidAmount = toNumber(sale?.paid_amount)
  const remainingAmount = toNumber(sale?.remaining_amount)

  const lines = []

  lines.push(centerTicketLine("Entreprise principale", width))
  lines.push(centerTicketLine(`${APP_NAME} ${APP_VERSION}`, width))
  lines.push(centerTicketLine("TICKET DE VENTE", width))
  lines.push("-".repeat(width))

  lines.push(`Vente : ${safeTicketText(sale?.sale_code)}`)
  lines.push(`Date  : ${formatDateTime(sale?.created_at)}`)
  lines.push(`Shop  : ${safeTicketText(sale?.store_name)}`)
  lines.push(`Org.  : ${safeTicketText(sale?.organization_name)}`)
  lines.push(`User  : ${safeTicketText(sale?.created_by_name, "RLSys")}`)
  lines.push("-".repeat(width))

  lines.push(`Client: ${safeTicketText(sale?.customer_name, "Client comptoir")}`)
  lines.push("-".repeat(width))
  lines.push("ARTICLES")
  lines.push("-".repeat(width))

  if (!items.length) {
    lines.push("Aucun article")
  } else {
    items.forEach((item) => {
      const productCode = safeTicketText(item.product_code)
      const productName = safeTicketText(item.product_name)
      const quantity = formatQuantity(item.quantity)
      const unitPrice = formatAmount(item.unit_price)
      const lineTotal = formatAmount(item.total_price)

      lines.push(productCode)

      if (productName.length > width) {
        lines.push(`${productName.slice(0, width - 3)}...`)
      } else {
        lines.push(productName)
      }

      lines.push(`Qte ${quantity} x ${unitPrice}`)
      lines.push(`Total ligne : ${lineTotal}`)
      lines.push("")
    })
  }

  lines.push("-".repeat(width))
  lines.push(`Total articles : ${formatQuantity(totalItems)}`)
  lines.push(`Sous-total     : ${formatAmount(subTotal)}`)
  lines.push(`Total          : ${formatAmount(totalAmount)}`)
  lines.push(`Paye           : ${formatAmount(paidAmount)}`)
  lines.push(`Reste          : ${formatAmount(remainingAmount)}`)
  lines.push("-".repeat(width))
  lines.push(`Statut : ${getStatusLabel(sale?.status, sale)}`)
  lines.push(`Paie.  : ${getStatusLabel(sale?.payment_status, sale)}`)
  lines.push("-".repeat(width))
  lines.push(centerTicketLine("Merci pour votre achat", width))
  lines.push(centerTicketLine("RLSys Business", width))
  lines.push("\n\n")

  return lines.join("\n")
}

function DetailMetric({ label, value, tone = "primary" }) {
  const toneMap = {
    primary: SALES_UI.primary,
    success: SALES_UI.success,
    warning: SALES_UI.warning,
    danger: SALES_UI.danger,
    neutral: "#475569",
  }

  return (
    <Box
      sx={{
        p: 1.8,
        borderRadius: 3,
        border: `1px solid ${SALES_UI.border}`,
        backgroundColor: SALES_UI.surface,
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
        borderLeft: `4px solid ${toneMap[tone] || SALES_UI.primary}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: SALES_UI.muted,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.35,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: SALES_UI.text,
          fontWeight: 900,
          fontSize: { xs: 21, sm: 24 },
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}


function ValidationMobileCard({
  row,
  updateValidationRow,
  applyRequestedPrice,
  resetToSystemPrice,
}) {
  const totalFinal = toNumber(row.final_unit_price) * toNumber(row.quantity)

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        mb: 1.5,
        border: `1px solid ${SALES_UI.border}`,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography fontWeight={900}>{row.product_code || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {row.product_name || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Quantité
              </Typography>
              <Typography fontWeight={700}>
                {formatQuantity(row.quantity)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Prix système
              </Typography>
              <Typography fontWeight={700}>
                {formatAmount(row.base_unit_price)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Prix souhaité client
              </Typography>
              <Typography fontWeight={700}>
                {row.requested_unit_price
                  ? formatAmount(row.requested_unit_price)
                  : "-"}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Total final
              </Typography>
              <Typography fontWeight={700}>{formatAmount(totalFinal)}</Typography>
            </Box>
          </Box>

          <TextField
            select
            size="small"
            fullWidth
            label="Décision Entreprise principale"
            value={row.discount_type || "none"}
            onChange={(e) =>
              updateValidationRow(
                row.sale_item_id,
                "discount_type",
                e.target.value
              )
            }
          >
            <MenuItem value="none">Garder prix système</MenuItem>
            <MenuItem value="manual">Saisir prix final négocié</MenuItem>
            <MenuItem value="percent">Accorder remise %</MenuItem>
            <MenuItem value="amount">Accorder remise montant</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label={
                row.discount_type === "percent"
                  ? "Remise (%)"
                  : "Montant remise"
              }
              value={row.discount_value}
              onChange={(e) =>
                updateValidationRow(
                  row.sale_item_id,
                  "discount_value",
                  e.target.value
                )
              }
              disabled={["none", "manual"].includes(row.discount_type)}
              slotProps={{
                htmlInput: {
                  min: 0,
                  step: "0.01",
                },
              }}
            />

            <TextField
              size="small"
              fullWidth
              type="number"
              label="Prix final"
              value={row.final_unit_price}
              onChange={(e) =>
                updateValidationRow(
                  row.sale_item_id,
                  "final_unit_price",
                  e.target.value
                )
              }
              slotProps={{
                htmlInput: {
                  min: 0,
                  step: "0.01",
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={!row.requested_unit_price}
              onClick={() => applyRequestedPrice(row)}
            >
              Accepter prix client
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => resetToSystemPrice(row)}
            >
              Prix système
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

function WarehouseSelectionCard({
  item,
  warehouseAssignments,
  setWarehouseAssignments,
}) {
  const options = Array.isArray(item?.warehouse_options)
    ? item.warehouse_options
    : []

  const selectedValue = warehouseAssignments[String(item.id)] || ""

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        mb: 1.5,
        border: `1px solid ${SALES_UI.border}`,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography fontWeight={900}>
              {item.product_code} - {item.product_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quantité à livrer : {formatQuantity(item.quantity)}
            </Typography>
          </Box>

          <TextField
            select
            fullWidth
            size="small"
            label="Entrepôt de sortie"
            value={selectedValue}
            onChange={(e) =>
              setWarehouseAssignments((prev) => ({
                ...prev,
                [String(item.id)]: e.target.value,
              }))
            }
          >
            <MenuItem value="">Sélectionner l'entrepôt</MenuItem>

            {options.map((warehouse) => (
              <MenuItem
                key={warehouse.warehouse_id}
                value={String(warehouse.warehouse_id)}
                disabled={
                  toNumber(warehouse.available_quantity) <
                  toNumber(item.quantity)
                }
              >
                {warehouse.warehouse_code} - {warehouse.warehouse_name} | Stock :{" "}
                {formatQuantity(warehouse.available_quantity)}
              </MenuItem>
            ))}
          </TextField>

          {toNumber(item.unassigned_stock) > 0 && (
            <Alert severity="warning" variant="outlined">
              Stock sans entrepôt détecté : {formatQuantity(item.unassigned_stock)}.
              Rattachez ce stock à un entrepôt propre avant livraison.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function SaleDetailPage() {
  const { saleId } = useParams()
  const navigate = useNavigate()

  const [sale, setSale] = useState(null)
  const [items, setItems] = useState([])
  const [validationRows, setValidationRows] = useState([])
  const [warehouseAssignments, setWarehouseAssignments] = useState({})
  const [validationNote, setValidationNote] = useState("")
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  useEffect(() => {
    loadSale()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId])

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const loadSale = async () => {
    try {
      setLoading(true)

      const data = await getSaleById(saleId)
      const loadedSale = data?.sale || null
      const loadedItems = Array.isArray(data?.items) ? data.items : []

      setSale(loadedSale)
      setItems(loadedItems)
      setValidationRows(buildValidationRows(loadedItems))

      const nextAssignments = {}

      loadedItems.forEach((item) => {
        const bestWarehouse = getBestWarehouseForItem(item)

        if (bestWarehouse) {
          nextAssignments[String(item.id)] = bestWarehouse
        }
      })

      setWarehouseAssignments(nextAssignments)
    } catch (err) {
      console.error(err)
      showSnackbar("Impossible de charger le détail.", "error")
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = async () => {
    const isNativeApp =
      typeof Capacitor?.isNativePlatform === "function"
        ? Capacitor.isNativePlatform()
        : false

    if (!isNativeApp) {
      window.print()
      return
    }

    try {
      const availability = await SunmiPrinter.isAvailable()

      if (!availability?.available) {
        window.print()
        return
      }

      const ticketText = buildThermalSaleTicket(sale, items)

      await SunmiPrinter.printText({
        text: ticketText,
      })

      showSnackbar("Ticket imprimé sur le POS SUNMI.", "success")
    } catch (error) {
      console.error("Erreur impression SUNMI:", error)

      showSnackbar(
        "Impression SUNMI indisponible. Ouverture de l'impression classique.",
        "warning"
      )

      window.print()
    }
  }

  const updateValidationRow = (saleItemId, field, value) => {
    setValidationRows((prev) =>
      prev.map((row) => {
        if (row.sale_item_id !== saleItemId) {
          return row
        }

        const nextRow = {
          ...row,
          [field]: value,
        }

        const basePrice = toNumber(nextRow.base_unit_price)

        if (field === "discount_type") {
          if (value === "none") {
            nextRow.discount_value = 0
            nextRow.final_unit_price = basePrice.toFixed(2)
          }

          if (value === "manual") {
            nextRow.discount_value = Math.max(
              0,
              basePrice - toNumber(nextRow.final_unit_price)
            ).toFixed(2)
          }

          if (value === "percent" || value === "amount") {
            const discountValue = toNumber(nextRow.discount_value)

            if (value === "percent") {
              nextRow.final_unit_price = Math.max(
                0,
                basePrice - (basePrice * discountValue) / 100
              ).toFixed(2)
            }

            if (value === "amount") {
              nextRow.final_unit_price = Math.max(
                0,
                basePrice - discountValue
              ).toFixed(2)
            }
          }
        }

        if (field === "discount_value") {
          const discountValue = toNumber(value)

          if (nextRow.discount_type === "percent") {
            nextRow.final_unit_price = Math.max(
              0,
              basePrice - (basePrice * discountValue) / 100
            ).toFixed(2)
          }

          if (nextRow.discount_type === "amount") {
            nextRow.final_unit_price = Math.max(
              0,
              basePrice - discountValue
            ).toFixed(2)
          }
        }

        if (field === "final_unit_price") {
          const finalPrice = toNumber(value)

          if (!nearlyEqual(finalPrice, basePrice)) {
            nextRow.discount_type = "manual"
            nextRow.discount_value = Math.max(0, basePrice - finalPrice).toFixed(2)
          } else {
            nextRow.discount_type = "none"
            nextRow.discount_value = 0
          }
        }

        return nextRow
      })
    )
  }

  const applyRequestedPrice = (row) => {
    if (!row.requested_unit_price || toNumber(row.requested_unit_price) <= 0) {
      return
    }

    const requestedPrice = toNumber(row.requested_unit_price)

    setValidationRows((prev) =>
      prev.map((current) =>
        current.sale_item_id === row.sale_item_id
          ? {
              ...current,
              final_unit_price: requestedPrice.toFixed(2),
              discount_type: "manual",
              discount_value: Math.max(
                0,
                toNumber(current.base_unit_price) - requestedPrice
              ).toFixed(2),
            }
          : current
      )
    )
  }

  const resetToSystemPrice = (row) => {
    setValidationRows((prev) =>
      prev.map((current) =>
        current.sale_item_id === row.sale_item_id
          ? {
              ...current,
              final_unit_price: toNumber(current.base_unit_price).toFixed(2),
              discount_type: "none",
              discount_value: 0,
            }
          : current
      )
    )
  }

  const submitValidation = async () => {
    const invalidRow = validationRows.find(
      (row) => toNumber(row.final_unit_price) <= 0
    )

    if (invalidRow) {
      showSnackbar(
        `Prix final invalide pour ${invalidRow.product_code || "un produit"}.`,
        "warning"
      )
      return
    }

    try {
      setValidating(true)

      const payload = {
        action: "validate",
        validation_note: validationNote.trim() || null,
        items: validationRows.map((row) => ({
          sale_item_id: row.sale_item_id,
          base_unit_price: toNumber(row.base_unit_price),
          requested_unit_price: row.requested_unit_price
            ? toNumber(row.requested_unit_price)
            : null,
          final_unit_price: toNumber(row.final_unit_price),
          discount_type: row.discount_type || "none",
          discount_value: toNumber(row.discount_value),
        })),
      }

      await validateAgentOpportunity(saleId, payload)

      showSnackbar(
        "Demande confirmée au prix final. Elle attend maintenant le paiement client.",
        "success"
      )

      await loadSale()
    } catch (err) {
      console.error(err)
      showSnackbar(err.message || "Erreur validation.", "error")
    } finally {
      setValidating(false)
    }
  }

  const rejectOpportunity = async () => {
    const confirmed = window.confirm(
      "Confirmer le refus de cette demande commerciale ?"
    )

    if (!confirmed) return

    try {
      setValidating(true)

      await validateAgentOpportunity(saleId, {
        action: "reject",
        validation_note: validationNote.trim() || null,
      })

      showSnackbar("Demande commerciale refusée.", "success")

      await loadSale()
    } catch (err) {
      console.error(err)
      showSnackbar(err.message || "Erreur rejet.", "error")
    } finally {
      setValidating(false)
    }
  }

  const confirmDelivery = async () => {
    const missingWarehouse = items.find((item) => {
      return !warehouseAssignments[String(item.id)]
    })

    if (missingWarehouse) {
      showSnackbar(
        `Choisissez l'entrepôt de sortie pour ${missingWarehouse.product_name}.`,
        "warning"
      )
      return
    }

    const confirmed = window.confirm(
      "Confirmer la livraison au client et sortir le stock depuis les entrepôts sélectionnés ?"
    )

    if (!confirmed) return

    try {
      setValidating(true)

      await confirmAgentDelivery(saleId, {
        delivery_note: validationNote.trim() || null,
        warehouse_assignments: warehouseAssignments,
        item_warehouses: items.map((item) => ({
          sale_item_id: item.id,
          product_id: item.product_id,
          warehouse_id: Number(warehouseAssignments[String(item.id)]),
        })),
      })

      showSnackbar(
        "Livraison confirmée. La demande devient une vente confirmée.",
        "success"
      )

      await loadSale()
    } catch (err) {
      console.error(err)
      showSnackbar(err.message || "Erreur livraison.", "error")
    } finally {
      setValidating(false)
    }
  }

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
  }, [items])

  const subTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + Number(item.total_price || 0), 0)
  }, [items])

  const validationTotal = useMemo(() => {
    return validationRows.reduce((sum, row) => {
      return sum + toNumber(row.final_unit_price) * toNumber(row.quantity)
    }, 0)
  }, [validationRows])

  const validationTotalSystem = useMemo(() => {
    return validationRows.reduce((sum, row) => {
      return sum + toNumber(row.base_unit_price) * toNumber(row.quantity)
    }, 0)
  }, [validationRows])

  const validationDifference = validationTotal - validationTotalSystem

  const paidAmount = Number(sale?.paid_amount || 0)
  const remainingAmount = Number(sale?.remaining_amount || 0)
  const totalAmount = Number(sale?.total_amount || 0)
  const vatAmount = Math.max(0, totalAmount - subTotal)

  if (loading) {
    return (
      <MainLayout>
        <Box
          sx={{
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={28} />
            <Typography>Chargement du détail...</Typography>
          </Stack>
        </Box>
      </MainLayout>
    )
  }

  if (!sale) {
    return (
      <MainLayout>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Donnée introuvable
          </Typography>

          <Typography variant="body2" color="text.secondary" mt={1}>
            La demande ou la vente demandée n'a pas pu être chargée.
          </Typography>

          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
            onClick={() => navigate("/sales")}
          >
            Retour
          </Button>
        </Box>
      </MainLayout>
    )
  }

  const agentOpportunity = isAgentOpportunity(sale)
  const canValidate = canValidateAgentOpportunity(sale)
  const canDeliver = canConfirmAgentDelivery(sale)

  return (
    <MainLayout>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }

            .sale-print-area {
              box-shadow: none !important;
            }
          }
        `}
      </style>

      <Box
        className="sale-print-area"
        sx={{
          maxWidth: 1540,
          width: "100%",
          mx: "auto",
          pb: 3,
        }}
      >
        <Box
          className="no-print"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "flex-start" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                color: SALES_UI.primary,
                fontWeight: 900,
                letterSpacing: 1,
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              VENTES
            </Typography>

            <Typography
              sx={{
                color: SALES_UI.text,
                fontSize: { xs: 26, sm: 30, md: 34 },
                lineHeight: 1.08,
                fontWeight: 900,
                mb: 0.6,
              }}
            >
              {agentOpportunity
                ? "Détail demande commerciale"
                : "Détail vente / commande"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {agentOpportunity
                ? `Client amené par agent commercial - ${sale.sale_code}`
                : `Détails de ligne pour ${sale.sale_code}`}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              "& .MuiButton-root": {
                minHeight: 42,
                borderRadius: 2.5,
                fontWeight: 800,
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/sales")}
            >
              Retour
            </Button>

            {canEditSale(sale) && !canValidate && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales/${sale.id}/edit`)}
              >
                Edit
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Imprimer
            </Button>
          </Stack>
        </Box>

        {agentOpportunity && (
          <Alert
            severity={sale.status === "pending_price_validation" ? "warning" : "info"}
            variant="outlined"
            sx={{ mb: 3 }}
            className="no-print"
          >
            Cette demande vient d'un agent commercial. L’agent amène le client et
            enregistre le besoin. Entreprise principale décide le prix final, le client paie,
            puis la sortie stock se fait depuis un entrepôt Entreprise principale.
          </Alert>
        )}

        <Box
          className="no-print"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <DetailMetric
            label="Montant total"
            value={formatAmount(totalAmount)}
            tone="primary"
          />
          <DetailMetric
            label="Montant payé"
            value={formatAmount(paidAmount)}
            tone="success"
          />
          <DetailMetric
            label="Reste à payer"
            value={formatAmount(remainingAmount)}
            tone={remainingAmount > 0 ? "warning" : "success"}
          />
          <DetailMetric
            label="Articles"
            value={formatQuantity(totalItems)}
            tone="neutral"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3.5,
            mb: 2.5,
            border: `1px solid ${SALES_UI.border}`,
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
            mb={3}
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <ReceiptLongIcon color="primary" />
                <Typography variant="h5" fontWeight="bold">
                  {sale.sale_code}
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Créé le {formatDateTime(sale.created_at)}
              </Typography>

              <Typography
                variant="caption"
                color="primary.main"
                fontWeight={800}
                sx={{ display: "block", mt: 0.75 }}
              >
                {APP_NAME} {APP_VERSION} — {APP_CONTEXT}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              <Chip
                label={`Statut: ${getStatusLabel(sale.status, sale)}`}
                color={getStatusColor(sale.status)}
                variant="outlined"
              />

              <Chip
                label={`Paiement: ${getStatusLabel(sale.payment_status, sale)}`}
                color={getStatusColor(sale.payment_status)}
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Client
                </Typography>
              </Stack>

              <Typography mb={0.75}>
                <strong>Nom:</strong> {sale.customer_name || "Client comptoir"}
              </Typography>

              <Typography mb={0.75}>
                <strong>Client ID:</strong> {sale.customer_id || "-"}
              </Typography>

              <Typography mb={0.75}>
                <strong>Nature:</strong>{" "}
                {agentOpportunity ? "Demande commerciale agent" : sale.sale_type || "-"}
              </Typography>

              {agentOpportunity && (
                <Typography mb={0.75}>
                  <strong>Agent commercial:</strong>{" "}
                  {sale.created_by_name || "-"}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <StoreIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Informations internes
                </Typography>
              </Stack>

              <Typography mb={0.75}>
                <strong>Lieu interne:</strong> {sale.store_name || "-"}
              </Typography>

              <Typography mb={0.75}>
                <strong>Organisation:</strong> {sale.organization_name || "-"}
              </Typography>

              <Typography mb={0.75}>
                <strong>Notes:</strong> {sale.notes || "-"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {canValidate && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3.5,
              mb: 2.5,
              border: `1px solid ${SALES_UI.border}`,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
            }}
            className="no-print"
          >
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Décision commerciale Entreprise principale
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Ici, Entreprise principale décide le prix final. Vous pouvez garder le prix
              système, accepter le prix souhaité par le client, accorder une
              remise ou saisir un prix final négocié.
            </Typography>

            <Box sx={{ display: { xs: "block", md: "none" } }}>
              {validationRows.map((row) => (
                <ValidationMobileCard
                  key={row.sale_item_id}
                  row={row}
                  updateValidationRow={updateValidationRow}
                  applyRequestedPrice={applyRequestedPrice}
                  resetToSystemPrice={resetToSystemPrice}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "100%",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 1100 }}>
                <TableHead sx={{ backgroundColor: SALES_UI.soft }}>
                  <TableRow>
                    <TableCell>Produit</TableCell>
                    <TableCell align="right">Qté</TableCell>
                    <TableCell align="right">Prix système</TableCell>
                    <TableCell align="right">Prix souhaité client</TableCell>
                    <TableCell>Décision Entreprise principale</TableCell>
                    <TableCell align="right">Valeur remise</TableCell>
                    <TableCell align="right">Prix final décidé</TableCell>
                    <TableCell align="right">Total final</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {validationRows.map((row) => (
                    <TableRow key={row.sale_item_id}>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {row.product_code || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.product_name || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        {formatQuantity(row.quantity)}
                      </TableCell>

                      <TableCell align="right">
                        {formatAmount(row.base_unit_price)}
                      </TableCell>

                      <TableCell align="right">
                        {row.requested_unit_price
                          ? formatAmount(row.requested_unit_price)
                          : "-"}
                      </TableCell>

                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.discount_type || "none"}
                          onChange={(e) =>
                            updateValidationRow(
                              row.sale_item_id,
                              "discount_type",
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value="none">Garder prix système</MenuItem>
                          <MenuItem value="manual">
                            Saisir prix final négocié
                          </MenuItem>
                          <MenuItem value="percent">Accorder remise %</MenuItem>
                          <MenuItem value="amount">
                            Accorder remise montant
                          </MenuItem>
                        </TextField>
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={row.discount_value}
                          onChange={(e) =>
                            updateValidationRow(
                              row.sale_item_id,
                              "discount_value",
                              e.target.value
                            )
                          }
                          disabled={["none", "manual"].includes(row.discount_type)}
                          sx={{ width: 130 }}
                          slotProps={{
                            htmlInput: {
                              min: 0,
                              step: "0.01",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={row.final_unit_price}
                          onChange={(e) =>
                            updateValidationRow(
                              row.sale_item_id,
                              "final_unit_price",
                              e.target.value
                            )
                          }
                          sx={{ width: 140 }}
                          slotProps={{
                            htmlInput: {
                              min: 0,
                              step: "0.01",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        {formatAmount(
                          toNumber(row.final_unit_price) * toNumber(row.quantity)
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!row.requested_unit_price}
                            onClick={() => applyRequestedPrice(row)}
                          >
                            Accepter prix client
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => resetToSystemPrice(row)}
                          >
                            Prix système
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Note de décision"
              value={validationNote}
              onChange={(e) => setValidationNote(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Exemple : prix système conservé, remise accordée après négociation, prix client accepté..."
            />

            <Box
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                backgroundColor: "#f8fafc",
                border: "1px solid #e5e7eb",
              }}
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Typography variant="body1">
                  Montant système :{" "}
                  <strong>{formatAmount(validationTotalSystem)}</strong>
                </Typography>

                <Typography variant="body1">
                  Montant décidé :{" "}
                  <strong>{formatAmount(validationTotal)}</strong>
                </Typography>

                <Typography
                  variant="body1"
                  color={validationDifference < 0 ? "error" : "success.main"}
                >
                  Écart : <strong>{formatAmount(validationDifference)}</strong>
                </Typography>
              </Stack>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={submitValidation}
                disabled={validating}
              >
                Confirmer la demande au prix final
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={rejectOpportunity}
                disabled={validating}
              >
                Refuser la demande
              </Button>
            </Stack>
          </Paper>
        )}

        {agentOpportunity && canDeliver && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3.5,
              mb: 2.5,
              border: `1px solid ${SALES_UI.border}`,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
            }}
            className="no-print"
          >
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Livraison client
            </Typography>

            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              Le client a payé. La dernière étape est la livraison physique et
              la sortie stock. Pour une demande agent commercial, le stock sort
              depuis les entrepôts Entreprise principale, pas depuis un shop.
            </Alert>

            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <WarehouseIcon color="primary" />
              <Typography fontWeight={800}>
                Choisir l'entrepôt de sortie par produit
              </Typography>
            </Stack>

            {items.map((item) => (
              <WarehouseSelectionCard
                key={item.id}
                item={item}
                warehouseAssignments={warehouseAssignments}
                setWarehouseAssignments={setWarehouseAssignments}
              />
            ))}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Note de livraison"
              value={validationNote}
              onChange={(e) => setValidationNote(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
              placeholder="Exemple : client servi depuis l'entrepôt Matelas, sortie stock validée..."
            />

            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={confirmDelivery}
              disabled={validating}
            >
              Confirmer livraison / sortie stock
            </Button>
          </Paper>
        )}

        {agentOpportunity &&
          sale.status === "pending" &&
          sale.payment_status === "unpaid" && (
            <Alert severity="success" variant="outlined" sx={{ mb: 3 }}>
              Le prix final est décidé. La prochaine étape est
              l’enregistrement du paiement client par Entreprise principale.
            </Alert>
          )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
            mb: 2.5,
            border: `1px solid ${SALES_UI.border}`,
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
            <Typography variant="h6" fontWeight="bold">
              Articles
            </Typography>
          </Box>

          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: agentOpportunity ? 1050 : 900 }}>
              <TableHead sx={{ backgroundColor: SALES_UI.soft }}>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Produit</TableCell>
                  <TableCell align="right">Qté</TableCell>
                  {agentOpportunity && (
                    <TableCell align="right">Prix système</TableCell>
                  )}
                  {agentOpportunity && (
                    <TableCell align="right">Prix souhaité client</TableCell>
                  )}
                  <TableCell align="right">
                    {agentOpportunity ? "Prix final / actuel" : "Prix unitaire"}
                  </TableCell>
                  {agentOpportunity && (
                    <TableCell align="right">Remise / écart</TableCell>
                  )}
                  <TableCell align="right">Total ligne</TableCell>
                  {agentOpportunity && <TableCell>Statut prix</TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={agentOpportunity ? 9 : 5}>
                      <Typography color="text.secondary">
                        Aucun article trouvé
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.product_code || "-"}</TableCell>
                      <TableCell>{item.product_name || "-"}</TableCell>
                      <TableCell align="right">
                        {formatQuantity(item.quantity)}
                      </TableCell>

                      {agentOpportunity && (
                        <TableCell align="right">
                          {formatAmount(
                            item.base_unit_price ||
                              item.selling_price ||
                              item.unit_price
                          )}
                        </TableCell>
                      )}

                      {agentOpportunity && (
                        <TableCell align="right">
                          {item.requested_unit_price
                            ? formatAmount(item.requested_unit_price)
                            : "-"}
                        </TableCell>
                      )}

                      <TableCell align="right">
                        {formatAmount(item.unit_price)}
                      </TableCell>

                      {agentOpportunity && (
                        <TableCell align="right">
                          {item.discount_amount
                            ? formatAmount(item.discount_amount)
                            : "-"}
                        </TableCell>
                      )}

                      <TableCell align="right">
                        {formatAmount(item.total_price)}
                      </TableCell>

                      {agentOpportunity && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              item.price_status === "price_validated"
                                ? "Prix décidé"
                                : "À décider"
                            }
                            color={
                              item.price_status === "price_validated"
                                ? "success"
                                : "warning"
                            }
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "flex-start" }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3.5,
              flex: 1,
              border: `1px solid ${SALES_UI.border}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Résumé
            </Typography>

            <Typography variant="body1" mb={1}>
              Total articles: <strong>{formatQuantity(totalItems)}</strong>
            </Typography>

            <Typography variant="body1" mb={1}>
              Sous-total articles: <strong>{formatAmount(subTotal)}</strong>
            </Typography>

            <Typography variant="body1" mb={1}>
              TVA / différence: <strong>{formatAmount(vatAmount)}</strong>
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3.5,
              minWidth: { xs: "100%", lg: 380 },
              border: `1px solid ${SALES_UI.border}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Paiement
            </Typography>

            <Typography variant="body1" mb={1}>
              Montant payé: <strong>{formatAmount(paidAmount)}</strong>
            </Typography>

            <Typography variant="body1" mb={1}>
              Reste à payer: <strong>{formatAmount(remainingAmount)}</strong>
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h5" fontWeight="bold">
              Total: {formatAmount(totalAmount)}
            </Typography>
          </Paper>
        </Stack>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}